import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    paginationModel: { page: 0, pageSize: 100 },
    rowCount: 0,
    allRoutes: [],
    il_route: [],
};

const slicePage = (all, page, pageSize) => {
    const start = page * pageSize;
    return all.slice(start, start + pageSize);
};

const routeSlice = createSlice({
    name: 'routes',
    initialState,
    reducers: {
        initRoute: () => initialState,

        // Set full dataset once
        setRoute: (state, action) => {
            const payload = action.payload || {};
            state.allRoutes = payload.il_route ?? [];
            state.rowCount = payload.rowCount ?? state.allRoutes.length;
            const { page, pageSize } = state.paginationModel;
            state.il_route = slicePage(state.allRoutes, page, pageSize);
        },

        receivedRoutes: (state, action) => {
            const { il_route, rowCount } = action.payload;
            // 1) 기존 routes를 복사해서 Map으로 관리 (id 기준 빠른 비교 가능)
            const routeMap = new Map(state.allRoutes.map((r) => [r.id, r]));

            // 2) 새로 들어온 rows로 갱신 or 추가
            il_route.forEach((nr) => {
                routeMap.set(nr.id, nr); // 동일 id 있으면 교체, 없으면 추가
            });

            // 3) 다시 배열로 변환하고 id 기준 내림차순 정렬
            state.allRoutes = Array.from(routeMap.values()).sort((a, b) => b.id - a.id);

            // 2) rowCount를 서버 응답 값으로 업데이트
            state.rowCount = rowCount;
            // 3) 현재 페이지 데이터는 새로 받아온 newRows로 덮어쓰기
            state.il_route = il_route;
        },

        // CRUD ops should update both allRoutes (source of truth) and il_route (current slice)
        addRoute: (state, action) => {
            state.allRoutes.unshift(action.payload);
            state.rowCount = state.allRoutes.length;
            const { page, pageSize } = state.paginationModel;
            state.il_route = slicePage(state.allRoutes, page, pageSize);
        },

        createAndAppendRoute: (state, action) => {
            const newRoute = {
                id: action.payload.id,
                pol_code: '',
                pol_name: '',
                pol_country: '',
                pod_code: '',
                pod_name: '',
                pod_country: '',
                leadtime: 1,
                isNew: true,
            };
            state.allRoutes.unshift(newRoute);
            state.rowCount = state.allRoutes.length;
            const { page, pageSize } = state.paginationModel;
            state.il_route = slicePage(state.allRoutes, page, pageSize);
        },

        updateRoute: (state, action) => {
            const { newRow, updatedRow } = action.payload;
            const normalized = {
                ...updatedRow,
                leadtime: Number(updatedRow.leadtime),
                isNew: false,
            };
            state.allRoutes = state.allRoutes.map((r) => (r.id === newRow.id ? normalized : r));
            const { page, pageSize } = state.paginationModel;
            state.il_route = slicePage(state.allRoutes, page, pageSize);
        },

        updatePol: (state, action) => {
            const { id, polValue } = action.payload;
            state.allRoutes = state.allRoutes.map((r) =>
                r.id === id
                    ? {
                          ...r,
                          pol_code: polValue.code,
                          pol_name: polValue.name,
                          pol_country: polValue.country,
                      }
                    : r
            );
            const { page, pageSize } = state.paginationModel;
            state.il_route = slicePage(state.allRoutes, page, pageSize);
        },

        updatePod: (state, action) => {
            const { id, podValue } = action.payload;
            state.allRoutes = state.allRoutes.map((r) =>
                r.id === id
                    ? {
                          ...r,
                          pod_code: podValue.code,
                          pod_name: podValue.name,
                          pod_country: podValue.country,
                      }
                    : r
            );
            const { page, pageSize } = state.paginationModel;
            state.il_route = slicePage(state.allRoutes, page, pageSize);
        },

        deleteRoute: (state, action) => {
            state.allRoutes = state.allRoutes.filter((r) => r.id !== action.payload);
            state.rowCount = state.allRoutes.length;
            const { page, pageSize } = state.paginationModel;
            // If current page is now out of range, pull it back
            const maxPage = Math.max(0, Math.ceil(state.rowCount / pageSize) - 1);
            state.paginationModel.page = Math.min(state.paginationModel.page, maxPage);
            state.il_route = slicePage(state.allRoutes, state.paginationModel.page, pageSize);
        },

        updatePaginationModel: (state, action) => {
            const { page, pageSize } = action.payload;
            const sizeChanged = pageSize !== state.paginationModel.pageSize;
            state.paginationModel.pageSize = pageSize;
            state.paginationModel.page = sizeChanged ? 0 : page;
            state.il_route = slicePage(state.allRoutes, state.paginationModel.page, state.paginationModel.pageSize);
        },
    },
});

export const {
    initRoute,
    setRoute,
    receivedRoutes,
    addRoute,
    createAndAppendRoute,
    updateRoute,
    updatePol,
    updatePod,
    deleteRoute,
    updatePaginationModel,
} = routeSlice.actions;

export default routeSlice.reducer;
